// /admin/launch-kit — Per-stay share kit, sent AFTER each stay goes live.
//
// For every active stay it generates a ready-to-send asset pack:
//   • The public link (defaults to gohimalayan.com — toggle to preview while testing)
//   • A pre-written IG caption
//   • A WhatsApp status / story text
//   • A LinkedIn post template (for hosts who use it)
//   • A QR code (via api.qrserver.com — free, no key)
//   • One-tap "Send this kit to the host on WhatsApp"
//
// Sent-status is tracked in localStorage so two people can split the work without
// a backend write. Refresh-safe. No new schema. No new endpoints.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Rocket, Send, Check, Copy, MessageCircle, Linkedin, Instagram,
  QrCode, ExternalLink, Search, Filter, RefreshCw, Lock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const pf = { fontFamily: "'Playfair Display', Georgia, serif" };
const LS_KEY = 'gohi_launch_kit_sent_v1';

const HASHTAGS = '#GoHimalayan #Himalayas #IncredibleIndia';

const captionTemplate = (s, link) => (
`We're now on GoHimalayan — a curated travel platform for hand-picked stays across the Himalayas.

${s.tagline ? `"${s.tagline}"` : `Tucked in ${s.valley || s.state}, our home is open to direct bookings — zero commission, real conversations.`}

Discover us here ↓
${link}

${HASHTAGS}`
);

const whatsAppStatusTemplate = (s, link) =>
`We're live on GoHimalayan — a curated travel platform for the Himalayas. Find us here: ${link}`;

const linkedInTemplate = (s, link) => (
`Happy to share — ${s.display_name} is now live on GoHimalayan, a curated travel platform for hand-picked stays across the Himalayas.

${s.display_name} in ${s.valley || s.state} is open for direct bookings — editorial taste and zero commission, the way mountain stays should be.

Read our story → ${link}

${HASHTAGS}`
);

// What we WhatsApp the HOST once the listing is live
const hostHandoffMessage = (s, link, qr) =>
`Hi ${s.host_name ? s.host_name.split(' ')[0] : 'there'} — great to have ${s.display_name} live on GoHimalayan 🌄

Your live link is here:
${link}

📸 IG / FB caption (copy-paste):
––––––––––––––
${captionTemplate(s, link)}
––––––––––––––

📱 WhatsApp status:
${whatsAppStatusTemplate(s, link)}

💼 LinkedIn (optional):
${linkedInTemplate(s, link)}

QR code (printable): ${qr}

Share whenever feels right — tag @gohimalayan and use #GoHimalayan so we can boost you back.

Thanks for being part of GoHimalayan 🙏
— GoHimalayan team`;

export default function AdminLaunchKit() {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);

  const [stays, setStays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [baseUrl, setBaseUrl] = useState('https://gohimalayan.com');  // toggle to preview while testing
  const [query, setQuery] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [showOnly, setShowOnly] = useState('all'); // all | unsent | sent
  const [expanded, setExpanded] = useState(null);

  const [sentMap, setSentMap] = useState(() => {
    // Pre-paint from localStorage cache so the UI doesn't flicker before the
    // server hydration arrives. Server is the authoritative source.
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  });
  const [pendingSent, setPendingSent] = useState({}); // gid → true while API in flight
  const [copied, setCopied] = useState({}); // { stayId_kind: true }

  // Hydrate sentMap from the server (authoritative, cross-device). We also
  // keep a localStorage mirror as an instant-paint fallback while the API
  // request is in flight.
  const hydrateSentFromStays = (stayList) => {
    const m = {};
    for (const s of stayList) {
      if (s.launch_kit_sent_at) m[s.gohimalayan_id] = true;
    }
    setSentMap(m);
    try { localStorage.setItem(LS_KEY, JSON.stringify(m)); } catch (e) { /* ignore */ }
  };

  const tryAuth = () => {
    if (password === 'gohimalayan2024') {
      setAuthed(true);
      loadStays();
    } else {
      setError('Wrong password');
    }
  };

  const loadStays = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/founders/links`);
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed to load stays');
      setStays(data.stays || []);
      hydrateSentFromStays(data.stays || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Filtered/searched list
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return stays.filter(s => {
      if (filterTier !== 'all' && s.partnership_status !== filterTier) return false;
      const gid = s.gohimalayan_id;
      const sent = !!sentMap[gid];
      if (showOnly === 'sent' && !sent) return false;
      if (showOnly === 'unsent' && sent) return false;
      if (!q) return true;
      return (s.display_name || '').toLowerCase().includes(q)
        || (s.host_name || '').toLowerCase().includes(q)
        || (s.valley || '').toLowerCase().includes(q)
        || (s.state || '').toLowerCase().includes(q);
    });
  }, [stays, query, filterTier, showOnly, sentMap]);

  const totalSent = Object.values(sentMap).filter(Boolean).length;
  const totalKitsReady = stays.filter(s => s.whatsapp_number && s.public_path && s.public_path !== '/discovered').length;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(c => ({ ...c, [key]: true }));
      setTimeout(() => setCopied(c => ({ ...c, [key]: false })), 1800);
    } catch (e) { /* ignore */ }
  };

  // Mark sent — optimistically toggle locally, then persist to server so other
  // devices/teammates see the same state. On failure, revert + show toast.
  const markSent = async (gid) => {
    const currentlySent = !!sentMap[gid];
    const next = !currentlySent;
    setSentMap(m => ({ ...m, [gid]: next }));            // optimistic
    setPendingSent(p => ({ ...p, [gid]: true }));
    try {
      const params = new URLSearchParams({
        password,
        gid,
        sent: next ? 'true' : 'false',
      });
      const r = await fetch(`${API}/api/launch-kit/mark-sent?${params.toString()}`, {
        method: 'POST',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // Mirror into localStorage so the page paints fast on next visit
      try {
        const raw = localStorage.getItem(LS_KEY);
        const m = raw ? JSON.parse(raw) : {};
        if (next) m[gid] = true; else delete m[gid];
        localStorage.setItem(LS_KEY, JSON.stringify(m));
      } catch (e) { /* ignore */ }
    } catch (e) {
      // Revert on failure
      setSentMap(m => ({ ...m, [gid]: currentlySent }));
      setError(`Couldn't save sent-status: ${e.message}. Tap again to retry.`);
      setTimeout(() => setError(''), 4000);
    } finally {
      setPendingSent(p => {
        const next = { ...p };
        delete next[gid];
        return next;
      });
    }
  };

  // ─── AUTH WALL ──────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-stone-200 p-8 max-w-sm w-full shadow-sm" data-testid="launchkit-auth">
          <div className="flex items-center gap-2 mb-4">
            <Lock size={18} className="text-amber-700" />
            <h1 className="text-xl font-light italic text-stone-900" style={pf}>Launch Kit · Admin</h1>
          </div>
          <p className="text-xs text-stone-500 mb-4">Per-stay share packs. Founder + teammate only.</p>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && tryAuth()}
            placeholder="Admin password"
            className="w-full px-3 py-2 border border-stone-300 rounded text-sm mb-3"
            data-testid="launchkit-password-input"
            autoFocus
          />
          {error && <p className="text-xs text-rose-700 mb-2">{error}</p>}
          <button
            onClick={tryAuth}
            className="w-full bg-stone-900 text-white text-sm py-2 rounded hover:bg-stone-800"
            data-testid="launchkit-auth-btn"
          >Unlock</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-stone-900 text-white px-6 md:px-12 py-10">
        <div className="max-w-7xl mx-auto">
          <Link to="/links" className="text-[11px] uppercase tracking-[0.3em] text-amber-200/70 hover:text-amber-200">← Back to links</Link>
          <div className="flex items-center gap-3 mt-5">
            <Rocket size={26} className="text-amber-300" strokeWidth={1.4} />
            <h1 className="text-3xl md:text-4xl font-light italic" style={pf}>Launch Kit · Share assets</h1>
          </div>
          <p className="text-stone-300 text-sm mt-3 max-w-2xl leading-relaxed">
            One row per host. Tap <b>&ldquo;Open kit&rdquo;</b> to see their asset pack — copy any block, or hit <b>&ldquo;WhatsApp the whole kit&rdquo;</b> to send everything to their phone in one shot.
          </p>

          {/* Stats strip */}
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <Stat label="active stays" value={stays.length} />
            <Stat label="kits ready (with WA + link)" value={totalKitsReady} />
            <Stat label="hosts sent" value={totalSent} highlight />
            <Stat label="remaining" value={Math.max(totalKitsReady - totalSent, 0)} />
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search stay / host / valley…"
              className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded text-sm"
              data-testid="launchkit-search"
            />
          </div>
          <Filter size={14} className="text-stone-500" />
          <select value={filterTier} onChange={e => setFilterTier(e.target.value)} className="px-2 py-2 border border-stone-300 rounded text-xs" data-testid="launchkit-filter-tier">
            <option value="all">All tiers</option>
            <option value="featured">Featured</option>
            <option value="partner">Collective</option>
            <option value="scraped">Scraped</option>
          </select>
          <select value={showOnly} onChange={e => setShowOnly(e.target.value)} className="px-2 py-2 border border-stone-300 rounded text-xs" data-testid="launchkit-filter-status">
            <option value="all">All</option>
            <option value="unsent">Unsent only</option>
            <option value="sent">Sent only</option>
          </select>
          <div className="flex items-center gap-1 text-xs ml-2 text-stone-500">
            base:
            <select value={baseUrl} onChange={e => setBaseUrl(e.target.value)} className="px-2 py-1.5 border border-stone-300 rounded text-xs" data-testid="launchkit-base-toggle">
              <option value="https://gohimalayan.com">gohimalayan.com</option>
              <option value={typeof window !== 'undefined' ? window.location.origin : ''}>preview (this domain)</option>
            </select>
          </div>
          <button onClick={loadStays} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-stone-100 hover:bg-stone-200 rounded" data-testid="launchkit-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* List */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
        {loading && <p className="text-sm text-stone-500">Loading…</p>}
        {error && <p className="text-sm text-rose-700">{error}</p>}

        {!loading && filtered.length === 0 && (
          <p className="text-sm text-stone-500 text-center py-12">No stays match the current filter.</p>
        )}

        <ul className="space-y-2" data-testid="launchkit-list">
          {filtered.map(s => {
            const gid = s.gohimalayan_id;
            const sent = !!sentMap[gid];
            const link = `${baseUrl}${s.public_path}`;
            // Caption: only the property name — strip ", Valley" suffix
            const captionName = (s.display_name || '').split(',')[0].trim();
            // Version-tag the QR url with updated_at so a re-curation invalidates
            // any browser/CDN cache automatically — prevents hosts from receiving
            // a stale QR that points to the old /discovered fallback.
            const qrVer = encodeURIComponent((s.updated_at || s.created_at || '').slice(0, 19));
            const qrUrl = `${API}/api/launch-kit/qr?gid=${encodeURIComponent(s.gohimalayan_id)}&base=${encodeURIComponent(baseUrl)}&name=${encodeURIComponent(captionName)}&v=${qrVer}`;
            const handoff = hostHandoffMessage(s, link, qrUrl);
            const hostWA = (s.whatsapp_number || '').replace(/\D/g, '');
            const canWhatsApp = hostWA.length >= 8;
            const isOpen = expanded === gid;

            return (
              <li key={gid} className={`bg-white border rounded-lg ${sent ? 'border-emerald-300 bg-emerald-50/40' : 'border-stone-200'}`} data-testid={`launchkit-row-${gid}`}>
                {/* Compact row */}
                <div className="flex items-center gap-3 px-4 py-3">
                  {s.thumb && <img src={s.thumb.startsWith('http') ? s.thumb : `${API}${s.thumb}`} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-stone-900 text-sm truncate">{s.display_name}</span>
                      <TierBadge tier={s.partnership_status} />
                      {sent && <span className="text-[10px] uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded">Sent ✓</span>}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5 truncate">
                      {s.host_name ? `${s.host_name} · ` : ''}{s.valley || s.state || '—'}
                      {s.whatsapp_number ? ` · +${s.whatsapp_number}` : ' · ⚠ no WhatsApp'}
                    </div>
                  </div>
                  <button
                    onClick={() => setExpanded(isOpen ? null : gid)}
                    className="text-xs bg-stone-900 text-white px-3 py-1.5 rounded hover:bg-stone-800"
                    data-testid={`launchkit-open-${gid}`}
                  >{isOpen ? 'Close' : 'Open kit'}</button>
                  <button
                    onClick={() => markSent(gid)}
                    disabled={!!pendingSent[gid]}
                    className={`text-xs px-2.5 py-1.5 rounded border transition-opacity ${sent ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white border-stone-300 text-stone-700 hover:bg-stone-50'} ${pendingSent[gid] ? 'opacity-60 cursor-wait' : ''}`}
                    data-testid={`launchkit-toggle-sent-${gid}`}
                    title={sent ? 'Mark as not sent' : 'Mark as sent'}
                  ><Check size={14} /></button>
                </div>

                {/* Expanded kit */}
                {isOpen && (
                  <div className="border-t border-stone-200 px-4 py-5 bg-stone-50/50">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {/* QR + link */}
                      <div className="bg-white border border-stone-200 rounded p-4">
                        <p className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Their live link</p>
                        <div className="flex items-center gap-2 mb-3">
                          <a href={link} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-800 underline break-all flex-1">{link}</a>
                          <button onClick={() => copy(link, `${gid}_link`)} className="p-1.5 bg-stone-100 hover:bg-stone-200 rounded" title="Copy link">
                            {copied[`${gid}_link`] ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                          </button>
                        </div>
                        <img src={qrUrl} alt="QR" className="w-full max-w-[220px] mx-auto border border-stone-200 rounded" />
                        <div className="flex gap-2 mt-2 justify-center">
                          <a href={qrUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-stone-600 underline">Open full-size</a>
                          <span className="text-stone-300">·</span>
                          <a href={qrUrl} download={`gohimalayan-qr-${(s.booking_source || s.gohimalayan_id || 'stay')}.png`} className="text-[10px] text-stone-600 underline">Download PNG</a>
                        </div>
                      </div>

                      {/* IG caption */}
                      <div className="bg-white border border-stone-200 rounded p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-[10px] uppercase tracking-widest text-stone-500 inline-flex items-center gap-1.5"><Instagram size={11} /> IG / FB caption</p>
                          <button onClick={() => copy(captionTemplate(s, link), `${gid}_ig`)} className="text-[10px] inline-flex items-center gap-1 px-2 py-1 bg-stone-900 text-white rounded hover:bg-stone-800">
                            {copied[`${gid}_ig`] ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                          </button>
                        </div>
                        <pre className="text-[11.5px] text-stone-700 whitespace-pre-wrap leading-relaxed font-sans">{captionTemplate(s, link)}</pre>
                      </div>

                      {/* WhatsApp status + LinkedIn */}
                      <div className="space-y-3">
                        <div className="bg-white border border-stone-200 rounded p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[10px] uppercase tracking-widest text-stone-500 inline-flex items-center gap-1.5"><MessageCircle size={11} /> WhatsApp status</p>
                            <button onClick={() => copy(whatsAppStatusTemplate(s, link), `${gid}_wa`)} className="text-[10px] inline-flex items-center gap-1 px-2 py-1 bg-stone-900 text-white rounded hover:bg-stone-800">
                              {copied[`${gid}_wa`] ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                            </button>
                          </div>
                          <p className="text-[11.5px] text-stone-700 leading-relaxed">{whatsAppStatusTemplate(s, link)}</p>
                        </div>
                        <div className="bg-white border border-stone-200 rounded p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[10px] uppercase tracking-widest text-stone-500 inline-flex items-center gap-1.5"><Linkedin size={11} /> LinkedIn (optional)</p>
                            <button onClick={() => copy(linkedInTemplate(s, link), `${gid}_li`)} className="text-[10px] inline-flex items-center gap-1 px-2 py-1 bg-stone-900 text-white rounded hover:bg-stone-800">
                              {copied[`${gid}_li`] ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                            </button>
                          </div>
                          <pre className="text-[11.5px] text-stone-700 whitespace-pre-wrap leading-relaxed font-sans">{linkedInTemplate(s, link)}</pre>
                        </div>
                      </div>
                    </div>

                    {/* Big actions */}
                    <div className="flex flex-col sm:flex-row gap-2 mt-5">
                      {canWhatsApp ? (
                        <a
                          href={`https://wa.me/${hostWA}?text=${encodeURIComponent(handoff)}`}
                          target="_blank" rel="noopener noreferrer"
                          onClick={() => setSentMap(m => ({ ...m, [gid]: true }))}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700"
                          data-testid={`launchkit-send-wa-${gid}`}
                        >
                          <Send size={14} /> WhatsApp the whole kit to {s.host_name?.split(' ')[0] || 'host'}
                        </a>
                      ) : (
                        <div className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-stone-100 text-stone-500 text-sm rounded border border-dashed border-stone-300">
                          ⚠ No WhatsApp number on file. Add it via `/admin/review` first.
                        </div>
                      )}
                      <button
                        onClick={() => copy(handoff, `${gid}_full`)}
                        className="px-4 py-3 bg-stone-900 text-white text-sm rounded hover:bg-stone-800 inline-flex items-center gap-2"
                        data-testid={`launchkit-copy-full-${gid}`}
                      >
                        {copied[`${gid}_full`] ? <Check size={14} /> : <Copy size={14} />} Copy full kit
                      </button>
                      <a
                        href={link} target="_blank" rel="noopener noreferrer"
                        className="px-4 py-3 bg-white border border-stone-300 text-stone-700 text-sm rounded hover:bg-stone-50 inline-flex items-center gap-2"
                        data-testid={`launchkit-preview-${gid}`}
                      ><ExternalLink size={14} /> Preview link</a>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

const Stat = ({ label, value, highlight }) => (
  <div className={`px-3 py-2 rounded ${highlight ? 'bg-amber-300/20 border border-amber-300/40' : 'bg-stone-800 border border-stone-700'}`}>
    <div className="text-base font-semibold text-amber-100">{value}</div>
    <div className="text-[9.5px] uppercase tracking-widest text-stone-400">{label}</div>
  </div>
);

const TierBadge = ({ tier }) => {
  const map = {
    featured: 'bg-amber-100 text-amber-800 border-amber-200',
    partner: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    valley_partner: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    scraped: 'bg-stone-100 text-stone-600 border-stone-200',
  };
  const labels = { featured: 'Featured', partner: 'Collective', valley_partner: 'Collective', scraped: 'Discovered' };
  return <span className={`text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${map[tier] || 'bg-stone-100 text-stone-500 border-stone-200'}`}>{labels[tier] || tier}</span>;
};
